import { useCallback, useMemo, useState } from 'react';

/**
 * What is ticked in the listing.
 *
 * Kept keyed by kind rather than as one set of ids, because the API needs to
 * know which is which — a folder, a file and a sheet with the same id would
 * be three different things, and the bulk endpoints act on each differently.
 *
 * Cleared whenever the folder changes: a selection that survives navigation
 * is a selection nobody can see, and "delete selected" would then act on rows
 * that are no longer on screen.
 */

export type ItemKind = 'folder' | 'file' | 'spreadsheet' | 'document';

export interface Selection {
  folder: Set<string>;
  file: Set<string>;
  spreadsheet: Set<string>;
  document: Set<string>;
}

const empty = (): Selection => ({
  folder: new Set<string>(),
  file: new Set<string>(),
  spreadsheet: new Set<string>(),
  document: new Set<string>(),
});

export function useSelection() {
  const [selected, setSelected] = useState<Selection>(empty);

  const toggle = useCallback((kind: ItemKind, id: string) => {
    setSelected((current) => {
      const next = { ...current, [kind]: new Set(current[kind]) };
      if (next[kind].has(id)) next[kind].delete(id);
      else next[kind].add(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(empty()), []);

  const selectAll = useCallback((items: { kind: ItemKind; id: string }[]) => {
    setSelected(() => {
      const next = empty();
      for (const item of items) next[item.kind].add(item.id);
      return next;
    });
  }, []);

  const has = useCallback((kind: ItemKind, id: string) => selected[kind].has(id), [selected]);

  const count = useMemo(
    () =>
      selected.folder.size +
      selected.file.size +
      selected.spreadsheet.size +
      selected.document.size,
    [selected],
  );

  /** The shape the bulk endpoints take. */
  const payload = useMemo(
    () => ({
      folderIds: [...selected.folder],
      fileIds: [...selected.file],
      spreadsheetIds: [...selected.spreadsheet],
      documentIds: [...selected.document],
    }),
    [selected],
  );

  return { selected, toggle, clear, selectAll, has, count, payload };
}
